import * as THREE from 'three';
import { CameraRig, type CameraMode } from '../engine/CameraRig';
import { PerfMonitor } from '../engine/PerfMonitor';
import { Renderer } from '../engine/Renderer';
import { clamp } from '../engine/mathUtils';
import { QUALITY_PROFILES, type QualityLevel } from '../engine/quality';
import { InputHub } from '../input/InputHub';
import { KeyboardInput } from '../input/KeyboardInput';
import { PhysicsWorld } from '../physics/PhysicsWorld';
import { RaycastVehicle } from '../physics/RaycastVehicle';
import { getVehicleSpec } from '../vehicles/vehicleData';
import { VehicleView } from '../vehicles/VehicleView';
import { FREE_DRIVE_SPAWN, FREE_DRIVE_YAW, TestArea } from '../world/TestArea';
import { telemetry } from '../ui/telemetry';
import { MissionSystem } from '../missions/MissionSystem';
import { AudioEngine } from '../audio/AudioEngine';
import { ParkingSystem } from '../parking/ParkingSystem';
import type { GameMode } from '../save/settings';

/** Sekunden auf dem Dach, bis automatisch aufgerichtet wird. */
const AUTO_RESCUE_DELAY = 1.8;

export interface GameOptions {
  canvas: HTMLCanvasElement;
  quality: QualityLevel;
  vehicleId: string;
  paintColor: number;
  cameraMode: CameraMode;
  gameMode: GameMode;
  onCameraModeChange: (mode: CameraMode) => void;
  collectedIds: readonly string[];
  onCollect: (id: string) => void;
  onMissionComplete: (id: string, coins: number, stars: number) => void;
}

/**
 * Bindet Renderer, Physik, Welt und Eingabe zusammen und faehrt die
 * Spielschleife. Enthaelt keine React-Abhaengigkeit.
 */
export class Game {
  readonly input = new InputHub();
  readonly perf = new PerfMonitor();
  private readonly audio = new AudioEngine();

  private readonly renderer: Renderer;
  private readonly physics: PhysicsWorld;
  private readonly world: TestArea;
  private readonly missions: MissionSystem;
  private readonly parking: ParkingSystem;
  private readonly cameraRig = new CameraRig();
  private readonly keyboard: KeyboardInput;
  private readonly onCameraModeChange: (mode: CameraMode) => void;

  private vehicle: RaycastVehicle;
  private view: VehicleView;
  private paintColor: number;
  private gameMode: GameMode;

  private rafId = 0;
  private lastTime = 0;
  private running = false;
  private upsideDownFor = 0;
  private inputLockedUntil = 0;
  private orientationBlocked = false;

  private readonly onResize = (): void => {
    this.renderer.resize();
    const blocked = isSmallPortrait();
    if (blocked && !this.orientationBlocked) {
      this.input.clearTransient();
      this.keyboard.clear();
    }
    this.orientationBlocked = blocked;
  };
  private readonly onVisibility = (): void => {
    // Nach dem Tab-Wechsel nicht die verpasste Zeit nachsimulieren.
    this.lastTime = performance.now();
    this.input.clearTransient();
    this.keyboard.clear();
  };

  private constructor(
    renderer: Renderer,
    physics: PhysicsWorld,
    world: TestArea,
    missions: MissionSystem,
    parking: ParkingSystem,
    options: GameOptions,
  ) {
    this.renderer = renderer;
    this.physics = physics;
    this.world = world;
    this.missions = missions;
    this.parking = parking;
    this.paintColor = options.paintColor;
    this.gameMode = options.gameMode;
    this.onCameraModeChange = options.onCameraModeChange;
    this.cameraRig.setMode(options.cameraMode);

    const spec = getVehicleSpec(options.vehicleId);
    const spawn = options.gameMode === 'parking' ? parking.spawn : FREE_DRIVE_SPAWN;
    const spawnYaw = options.gameMode === 'parking' ? parking.spawnYaw : FREE_DRIVE_YAW;
    this.vehicle = new RaycastVehicle(physics.world, spec, spawn, spawnYaw);
    this.vehicle.setSpeedLimit(options.gameMode === 'parking' ? 18 : null);
    this.view = new VehicleView(this.vehicle, this.paintColor);
    renderer.scene.add(this.view.root);
    missions.setEnabled(options.gameMode === 'free');
    parking.setEnabled(options.gameMode === 'parking');

    this.keyboard = new KeyboardInput(this.input);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
    window.visualViewport?.addEventListener('resize', this.onResize);
    window.visualViewport?.addEventListener('scroll', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibility);
    document.addEventListener('fullscreenchange', this.onResize);
    this.onResize();
  }

  static async create(options: GameOptions): Promise<Game> {
    const profile = QUALITY_PROFILES[options.quality];
    const renderer = new Renderer(options.canvas, profile);
    const physics = await PhysicsWorld.create();
    const world = new TestArea(renderer.scene, physics.world);
    const missions = new MissionSystem(renderer.scene, options.collectedIds, {
      onCollect: options.onCollect,
      onMissionComplete: options.onMissionComplete,
    });
    const parking = new ParkingSystem(renderer.scene, physics.world, {
      origin: { x: 70, z: 67 },
      onTaskComplete: (task) => options.onMissionComplete(`parking-${task.id}`, task.rewardCoins, 1),
    });
    // Ein Leerschritt fuellt die Query-Pipeline, damit die Rad-Raycasts
    // schon im ersten Frame Boden finden.
    physics.world.step();
    return new Game(renderer, physics, world, missions, parking, options);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    const tick = (now: number): void => {
      this.rafId = requestAnimationFrame(tick);
      this.frame(now);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  /** Physik anhalten, Rendering laeuft weiter (Pausenmenue). */
  paused = false;

  private frame(now: number): void {
    const started = this.perf.beginFrame();
    const dt = clamp((now - this.lastTime) / 1000, 0, 0.1);
    this.lastTime = now;

    if (this.input.consumeCameraChange()) {
      this.onCameraModeChange(this.cameraRig.cycleMode());
    }
    if (this.input.consumeReset()) {
      this.respawn();
      this.upsideDownFor = 0;
    }

    if (!this.paused && !this.orientationBlocked && dt > 0) {
      const drive = now < this.inputLockedUntil ? NEUTRAL_DRIVE : this.input.combined;
      const physicsStart = performance.now();
      this.physics.step(dt, (fixedDt) => {
        this.view.capturePrevious();
        this.vehicle.update(drive, fixedDt);
      });
      this.perf.addPhysicsTime(performance.now() - physicsStart);
      this.view.readPhysics();
      this.world.syncDynamics();
      if (this.gameMode === 'free') {
        this.missions.update(this.view.root.position, now);
      } else {
        const rotation = this.vehicle.body.rotation();
        const position = this.vehicle.body.translation();
        const angular = this.vehicle.body.angvel();
        const status = this.parking.update({
          position,
          yaw: yawFromQuat(rotation),
          speedKmh: this.vehicle.speedKmh,
          angularSpeed: Math.hypot(angular.x, angular.y, angular.z),
          halfWidth: this.vehicle.spec.chassisHalfExtents.x,
          halfLength: this.vehicle.spec.chassisHalfExtents.z,
          uprightness: this.vehicle.uprightness,
          grounded: this.vehicle.wheels.some((wheel) => wheel.inContact),
        }, dt);
        telemetry.missionTitle = `🅿 ${status.taskIndex + 1}/${status.taskCount} · ${status.title}`;
        telemetry.missionHint = status.instruction;
        telemetry.missionProgress = status.holdProgress;
      }
      this.rescueIfNeeded(dt);
    }

    const drive = now < this.inputLockedUntil ? NEUTRAL_DRIVE : this.input.combined;
    this.audio.update(
      this.vehicle.speedKmh,
      drive.throttle,
      this.input.hornActive,
      this.paused || this.orientationBlocked,
    );
    this.view.update(this.physics.alpha, dt, drive.brake > 0.05 || drive.handbrake);

    const bodyPos = this.view.root.position;
    const linvel = this.vehicle.body.linvel();
    this.cameraRig.update(
      this.renderer.camera,
      {
        position: bodyPos,
        quaternion: this.view.root.quaternion,
        forwardSpeed: this.vehicle.forwardSpeed,
        topSpeedMs: this.vehicle.spec.topSpeedKmh / 3.6,
        roughness: clamp(Math.abs(linvel.y) / 6, 0, 1),
      },
      dt,
    );
    this.renderer.updateSunTarget(bodyPos);

    this.renderer.adaptRenderScale(this.perf.smoothedFps, dt);
    this.renderer.render();

    this.publishTelemetry(drive);
    this.perf.endFrame(started, dt, this.renderer.renderer);
    this.perf.snapshot.renderScale = Math.round(this.renderer.currentRenderScale * 100) / 100;
  }

  private rescueIfNeeded(dt: number): void {
    const position = this.vehicle.body.translation();
    const leftParkingCourse = this.gameMode === 'parking' && this.parking.isOutsideCourse(position);
    if (leftParkingCourse || this.world.isOutsideSafeWorld(position)) {
      this.respawn();
      this.upsideDownFor = 0;
      return;
    }
    if (this.vehicle.uprightness < 0.28) {
      this.upsideDownFor += dt;
      if (this.upsideDownFor > AUTO_RESCUE_DELAY) {
        this.respawn();
        this.upsideDownFor = 0;
      }
    } else {
      this.upsideDownFor = 0;
    }
  }

  private publishTelemetry(drive: { steer: number; throttle: number; brake: number; handbrake: boolean }): void {
    const speed = this.vehicle.forwardSpeed;
    telemetry.speedKmh = this.vehicle.speedKmh;
    telemetry.gear = speed < -0.4 ? 'R' : speed > 0.4 ? 'V' : 'N';
    telemetry.steer = drive.steer;
    telemetry.throttle = drive.throttle;
    telemetry.brake = drive.brake;
    telemetry.handbrake = drive.handbrake;
    telemetry.upright = this.vehicle.uprightness;
    telemetry.airborne = !this.vehicle.wheels.some((w) => w.inContact);
    telemetry.perf = this.perf.snapshot;
  }

  setQuality(quality: QualityLevel): void {
    const profile = QUALITY_PROFILES[quality];
    this.renderer.setProfile(profile);
    this.perf.snapshot.quality = profile.label;
  }

  setPaintColor(color: number): void {
    if (color === this.paintColor) return;
    this.paintColor = color;
    this.swapVehicle(this.vehicle.spec.id);
  }

  setCameraMode(mode: CameraMode): void {
    this.cameraRig.setMode(mode);
  }

  setGameMode(mode: GameMode): void {
    if (mode === this.gameMode) return;
    this.gameMode = mode;
    this.missions.setEnabled(mode === 'free');
    this.parking.setEnabled(mode === 'parking');
    if (mode === 'parking') this.parking.reset();
    else this.missions.reset();
    const spawn = mode === 'parking' ? this.parking.spawn : FREE_DRIVE_SPAWN;
    const yaw = mode === 'parking' ? this.parking.spawnYaw : FREE_DRIVE_YAW;
    this.vehicle.setSpawn(spawn, yaw);
    this.vehicle.setSpeedLimit(mode === 'parking' ? 18 : null);
    this.respawn();
  }

  get currentVehicleId(): string {
    return this.vehicle.spec.id;
  }

  /** Fahrzeugwechsel am aktuellen Ort, damit man Unterschiede direkt spuert. */
  swapVehicle(id: string): void {
    const spec = getVehicleSpec(id);
    const t = this.vehicle.body.translation();
    const yaw = yawFromQuat(this.vehicle.body.rotation());

    this.view.dispose();
    this.vehicle.dispose();

    this.vehicle = new RaycastVehicle(
      this.physics.world,
      spec,
      { x: t.x, y: Math.max(t.y, 1.2), z: t.z },
      yaw,
    );
    this.vehicle.setSpawn(
      this.gameMode === 'parking' ? this.parking.spawn : FREE_DRIVE_SPAWN,
      this.gameMode === 'parking' ? this.parking.spawnYaw : FREE_DRIVE_YAW,
    );
    this.vehicle.setSpeedLimit(this.gameMode === 'parking' ? 18 : null);
    this.view = new VehicleView(this.vehicle, this.paintColor);
    this.renderer.scene.add(this.view.root);
    this.cameraRig.snap();
  }

  respawn(): void {
    this.input.clearTransient();
    this.keyboard.clear();
    this.inputLockedUntil = performance.now() + 450;
    this.vehicle.respawn();
    this.cameraRig.snap();
  }

  enableAudio(): void {
    void this.audio.enable();
  }

  setMuted(muted: boolean): void { this.audio.setMuted(muted); }

  dispose(): void {
    this.stop();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    window.visualViewport?.removeEventListener('resize', this.onResize);
    window.visualViewport?.removeEventListener('scroll', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('fullscreenchange', this.onResize);
    this.keyboard.dispose();
    this.view.dispose();
    this.audio.dispose();
    this.physics.dispose();
    this.renderer.dispose();
  }
}

function yawFromQuat(q: { x: number; y: number; z: number; w: number }): number {
  const v = new THREE.Vector3(0, 0, 1).applyQuaternion(
    new THREE.Quaternion(q.x, q.y, q.z, q.w),
  );
  return Math.atan2(v.x, v.z);
}

function isSmallPortrait(): boolean {
  const viewport = window.visualViewport;
  const width = viewport?.width ?? window.innerWidth;
  const height = viewport?.height ?? window.innerHeight;
  return Math.min(width, height) < 560 && height > width;
}

const NEUTRAL_DRIVE = { steer: 0, throttle: 0, brake: 0, handbrake: false } as const;
